import { CodedProperty, NamedProperty } from "sprint-common-web";

import {
    getCloudMovieDetailsHref,
    getCloudTvSeriesDetailsHref,
    getCloudAncillaryDetailsHref,
    getOnPremMovieDetailsHref,
    getOnPremTvSeriesDetailsHref,
    getOnPremAncillaryDetailsHref
} from "../../../../assets/AssetsRoutes";
import { NoteItem } from "../../../../common/components/notes/hooks/state/NoteItem";
import { ContentReferenceType } from "../../../../common/types/ContentReferenceType";
import { getLanguageDisplayName } from "../../../../common/types/Language";
import { OrderContentReference } from "../../../../common/types/OrderContentReference";
import { RecordType } from "../../../../common/types/RecordType";
import { OrderAssetAvailability } from "../../../common/types/OrderAssetAvailability";
import { OrderAssetStorageType } from "../../../common/types/OrderAssetStorageType";
import { OrderResolutionDimensions } from "../../../common/types/OrderResolutionDimensions";
import {
    OrderRequestDashboardItem,
    OrderRequestSoundtrack,
    OrderRequestSubtitle,
    OrderRequestVideo
} from "../OrderRequestDashboardItem";

export type OrderRequestAssetItem =
    | OrderRequestVideoAssetItem
    | OrderRequestAudioAssetItem
    | OrderRequestSubtitleAssetItem;

interface OrderRequestAssetItemBase {
    recordType: RecordType;
    storageType: OrderAssetStorageType;
    name: string;
    assetId?: string;
    assetHref?: string;
    filePath?: string;
    fileType?: number;
    availability: OrderAssetAvailability;
    notes: NoteItem[];
}

export interface OrderRequestVideoAssetItem extends OrderRequestAssetItemBase {
    recordType: RecordType.Video;
    aspectRatio: NamedProperty;
    sourceResolution?: OrderResolutionDimensions;
    targetResolution?: OrderResolutionDimensions;
    subtitle: CodedProperty;
    nonMatchingVideos: OrderRequestNonMatchingVideoAssetItem[];
}

interface OrderRequestNonMatchingVideoAssetItem {
    assetId: string;
    subtitle: CodedProperty;
    aspectRatio: NamedProperty;
    resolution: OrderResolutionDimensions;
}

export interface OrderRequestAudioAssetItem extends OrderRequestAssetItemBase {
    recordType: RecordType.Audio;
    language: CodedProperty;
}

export interface OrderRequestSubtitleAssetItem extends OrderRequestAssetItemBase {
    recordType: RecordType.Subtitle;
    language: CodedProperty;
}

interface AssetDetailsParams {
    content: OrderContentReference | undefined;
    storageType: OrderAssetStorageType | undefined;
    assetId: string | undefined;
}

const getAssetDetailsHref = ({ storageType, content, assetId }: AssetDetailsParams): string | undefined => {
    if (!assetId || !content) {
        return undefined;
    }

    switch (content.type) {
        case ContentReferenceType.Movie:
            return storageType === OrderAssetStorageType.OnPrem
                ? getOnPremMovieDetailsHref(assetId, content.subtype)
                : getCloudMovieDetailsHref(assetId, content.subtype);

        case ContentReferenceType.TvSeries:
            return storageType === OrderAssetStorageType.OnPrem
                ? getOnPremTvSeriesDetailsHref(assetId)
                : getCloudTvSeriesDetailsHref(assetId);

        case ContentReferenceType.Ancillary:
            return storageType === OrderAssetStorageType.OnPrem
                ? getOnPremAncillaryDetailsHref(assetId, content.subtype)
                : getCloudAncillaryDetailsHref(assetId, content.subtype);

        default:
            throw new Error("Unsupported content reference type");
    }
};

export const getOrderRequestAssetsItems = (orderRequest: OrderRequestDashboardItem): OrderRequestAssetItem[] => {
    if (!orderRequest) return [];

    return [
        // Cloud assets
        mapToOrderRequestAssetVideoItem(orderRequest.cloudAssets.video, orderRequest, OrderAssetStorageType.Cloud),
        ...orderRequest.cloudAssets.soundtracks.map((soundtrack) =>
            mapToOrderRequestAssetAudioItem(soundtrack, orderRequest, OrderAssetStorageType.Cloud)
        ),
        ...orderRequest.cloudAssets.subtitles.map((subtitle) =>
            mapToOrderRequestAssetSubtitleItem(subtitle, orderRequest, OrderAssetStorageType.Cloud)
        ),

        // OnPrem assets
        mapToOrderRequestAssetVideoItem(orderRequest.onPremAssets.video, orderRequest, OrderAssetStorageType.OnPrem),
        ...orderRequest.onPremAssets.soundtracks.map((soundtrack) =>
            mapToOrderRequestAssetAudioItem(soundtrack, orderRequest, OrderAssetStorageType.OnPrem)
        ),
        ...orderRequest.onPremAssets.subtitles.map((subtitle) =>
            mapToOrderRequestAssetSubtitleItem(subtitle, orderRequest, OrderAssetStorageType.OnPrem)
        )
    ];
};

const mapToOrderRequestAssetVideoItem = (
    item: OrderRequestVideo,
    orderRequest: OrderRequestDashboardItem,
    storageType: OrderAssetStorageType
): OrderRequestVideoAssetItem => ({
    recordType: RecordType.Video,
    name: orderRequest.contentTitle ?? "-",
    assetId: item.assetId,
    assetHref: getAssetDetailsHref({
        content: orderRequest.content,
        assetId: item.assetId,
        storageType
    }),
    nonMatchingVideos: item.nonMatchingVideos,
    storageType,
    availability: item.availability,
    aspectRatio: item.aspectRatio,
    sourceResolution: item.sourceResolution,
    targetResolution: item.targetResolution,
    subtitle: item.subtitle,
    fileType: item.fileType,
    filePath: item.filePath,
    notes: orderRequest.assetNotes.filter((note) => note.subjectId === item.assetId)
});

const mapToOrderRequestAssetAudioItem = (
    item: OrderRequestSoundtrack,
    orderRequest: OrderRequestDashboardItem,
    storageType: OrderAssetStorageType
): OrderRequestAudioAssetItem => ({
    recordType: RecordType.Audio,
    name: getLanguageDisplayName(item.language),
    assetId: item.assetId,
    storageType,
    assetHref: getAssetDetailsHref({
        content: orderRequest.content,
        assetId: item.assetId,
        storageType
    }),
    availability: item.availability,
    language: item.language,
    notes: orderRequest.assetNotes.filter((note) => note.subjectId === item.assetId)
});

const mapToOrderRequestAssetSubtitleItem = (
    item: OrderRequestSubtitle,
    orderRequest: OrderRequestDashboardItem,
    storageType: OrderAssetStorageType
): OrderRequestSubtitleAssetItem => ({
    recordType: RecordType.Subtitle,
    name: getLanguageDisplayName(item.language),
    fileType: item.subtitleType,
    assetId: item.assetId,
    storageType,
    assetHref: getAssetDetailsHref({
        content: orderRequest.content,
        assetId: item.assetId,
        storageType
    }),
    availability: item.availability,
    language: item.language,
    notes: orderRequest.assetNotes.filter((note) => note.subjectId === item.assetId)
});
